'use client';

import { useEffect, useMemo, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import {
  ATTENDEE_TYPES,
  FIRST_TIME_ATTENDEE_TYPE,
  buildInquiryMessage,
  isReferralTargetType,
  type AttendeeType,
} from '@/lib/inquiryMessage';

type InquiryType = '入会希望' | 'イベント参加' | 'サービス相談' | 'その他';

interface FormState {
  name: string;
  email: string;
  type: '' | InquiryType;
  attendeeType: '' | AttendeeType;
  referrerName: string;
  noReferrer: boolean;
  message: string;
  agree: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  type?: string;
  attendeeType?: string;
  message?: string;
  agree?: string;
}

type InquiryResponse = {
  ok: boolean;
  id?: string;
  // leader はイベント経由の申し込みのときだけ意味を持つ（部長への通知を送ったか）。
  // 送れなくても問い合わせ自体は成立しているため、申込者への文言には使わない
  mailSent?: { admin?: boolean; user?: boolean; leader?: boolean };
};

/**
 * バリデーション関数
 * 目的: 入力内容の妥当性を検証し、エラーメッセージを返す
 * 引数: state - 現在のフォーム値
 * 戻り値: FormErrors - 各フィールドのエラーメッセージ（なければ空オブジェクト）
 */
function validate(state: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!state.name.trim()) errors.name = 'お名前を入力してください。';
  if (!state.email.trim()) {
    errors.email = 'メールアドレスを入力してください。';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
    errors.email = 'メールアドレスの形式が正しくありません。';
  }
  if (!state.type) errors.type = 'お問い合わせ種別を選択してください。';
  if (isReferralTargetType(state.type) && !state.attendeeType) {
    errors.attendeeType = 'ご自身の区分を選択してください。';
  }
  if (!state.message.trim()) errors.message = 'お問い合わせ内容を入力してください。';
  if (!state.agree) errors.agree = 'プライバシーポリシーに同意してください。';
  return errors;
}

/**
 * 送信処理
 * 目的: 入力の検証後、API に送信する
 * 引数:
 *  - state: FormState 現在のフォーム値
 *  - eventId: どのイベントへの申し込みかを示す ID（イベント詳細から来たときのみ）。
 *    本文テキストにも書いてあるが、サーバーが部長へ通知するには機械が読める形が必要。
 *    サーバー側は不正な値を無視するだけで、問い合わせを弾くことはしない。
 * 戻り値: Promise<InquiryResponse>
 * 例外: ネットワークエラーやサーバーエラー時に例外が発生する可能性あり
 */
async function submitInquiry(
  state: FormState,
  eventId: string | null
): Promise<InquiryResponse> {
  // Laravel APIへ送信
  const base = process.env.NEXT_PUBLIC_API_BASE || 'http://localhost:8081';
  const res = await fetch(`${base}/api/inquiry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: state.name,
      email: state.email,
      type: state.type,
      // 区分・紹介者は専用カラムを持たないため、本文の先頭に定型で埋め込んで送る
      message: buildInquiryMessage(state),
      agree: state.agree,
      ...(eventId ? { eventId } : {}),
    }),
    // お問い合わせは Cookie 認証を使わないため credentials は不要。
    // クロスオリジン + credentials は CORS 的に厳しく、Allow-Origin:* と両立できずエラーになりやすい。
  });
  if (!res.ok) {
    throw new Error('送信に失敗しました。しばらく時間をおいて再度お試しください。');
  }
  return (await res.json().catch(() => ({ ok: true }))) as InquiryResponse;
}

export default function ContactForm() {
  const searchParams = useSearchParams();
  const [state, setState] = useState<FormState>({
    name: '',
    email: '',
    type: '',
    attendeeType: '',
    referrerName: '',
    noReferrer: false,
    message: '',
    agree: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);

  const inquiryTypes = useMemo<InquiryType[]>(
    () => ['入会希望', 'イベント参加', 'サービス相談', 'その他'],
    []
  );

  /**
   * お問い合わせフォームの事前入力
   * - 目的: 他ページからの遷移時に、種別や本文をクエリから自動入力して入力負荷を下げる
   * - 引数: なし（URLクエリを参照）
   * - 戻り値: なし
   */
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const messageParam = searchParams.get('message');
    const eventTitle = searchParams.get('eventTitle');
    const eventId = searchParams.get('eventId');

    const normalizedType =
      typeParam && inquiryTypes.includes(typeParam as InquiryType)
        ? (typeParam as InquiryType)
        : undefined;

    const computedMessage =
      messageParam ??
      (eventTitle || eventId
        ? `【イベント参加のお問い合わせ】\nイベント: ${eventTitle ?? ''}\nイベントID: ${eventId ?? ''}\n\nご用件:\n`
        : undefined);

    // 既にユーザーが入力している場合は上書きしない
    setState((prev) => {
      let changed = false;
      const next = { ...prev };
      if (!prev.type && normalizedType) {
        next.type = normalizedType;
        changed = true;
      }
      if (!prev.message && computedMessage) {
        next.message = computedMessage;
        changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, inquiryTypes]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setState((prev) => {
      const next: FormState = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // 非表示になった項目は値も落とす（隠れたまま送信されるのを防ぐ）
      if (name === 'type' && !isReferralTargetType(value)) {
        next.attendeeType = '';
      }
      if (next.attendeeType !== FIRST_TIME_ATTENDEE_TYPE) {
        next.referrerName = '';
        next.noReferrer = false;
      }
      // 「紹介者はいません」を選んだら入力済みの名前は残さない
      if (name === 'noReferrer' && checked) {
        next.referrerName = '';
      }

      return next;
    });
  };

  /**
   * フォーム送信ハンドラ
   * 目的: クライアント側バリデーション→API送信→UI更新
   * 戻り値: Promise<void>
   */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDoneMessage(null);
    const nextErrors = validate(state);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSubmitting(true);
      // イベント詳細の「申し込む」から来たときだけクエリに eventId が付く
      const result = await submitInquiry(state, searchParams.get('eventId'));
      const mailOk =
        result?.mailSent?.admin !== false && result?.mailSent?.user !== false;
      setDoneMessage(
        mailOk
          ? '送信が完了しました。担当者より折り返しご連絡いたします。'
          : '送信は受け付けましたが、確認メールの送信に失敗した可能性があります。担当者より折り返しご連絡いたします。'
      );
      // 成功後は初期化
      setState({
        name: '',
        email: '',
        type: '',
        attendeeType: '',
        referrerName: '',
        noReferrer: false,
        message: '',
        agree: false,
      });
    } catch (err) {
      // ここではユーザーフレンドリーなメッセージと開発者向けログを両立
      console.error('[お問い合わせ送信エラー]', err);
      setErrors({ message: '送信に失敗しました。時間をおいて再度お試しください。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-white'>
      <Header />
      <main className='bg-washi'>
        {/* Hero */}
        <section className='relative w-full'>
          <div className='relative mx-auto w-full max-w-7xl px-6 py-10 md:py-16'>
            <div className='absolute inset-0 -z-10'>
              <Image
                src='/assets/img/pattern-fabric.png'
                alt=''
                fill
                className='object-cover opacity-60'
                priority
              />
            </div>
            <h1 className='text-center font-serif text-4xl text-gray-800 md:text-5xl'>
              お問い合わせ
            </h1>
            <p className='mx-auto mt-6 max-w-3xl text-center text-lg leading-8 text-gray-700/80'>
              入会希望・イベント参加・サービス相談についてはこちらからご連絡ください。
            </p>
          </div>
        </section>

        {/* Form */}
        <section className='mx-auto w-full max-w-7xl px-6 pb-14 md:pb-20'>
          <div className='mx-auto max-w-3xl border border-gray-100 bg-white p-6 shadow-sm md:p-8'>
            <form onSubmit={onSubmit} noValidate>
              <div className='space-y-6'>
                {/* 名前 */}
                <div>
                  <label htmlFor='name' className='block text-sm font-bold text-gray-800'>
                    名前<span className='ml-1 text-rose-500'>*</span>
                  </label>
                  <input
                    id='name'
                    name='name'
                    type='text'
                    placeholder='お名前を入力してください'
                    value={state.name}
                    onChange={onChange}
                    aria-invalid={!!errors.name}
                    className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/60'
                  />
                  {errors.name && (
                    <p className='mt-1 text-sm text-rose-600'>{errors.name}</p>
                  )}
                </div>

                {/* メール */}
                <div>
                  <label htmlFor='email' className='block text-sm font-bold text-gray-800'>
                    メールアドレス<span className='ml-1 text-rose-500'>*</span>
                  </label>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    placeholder='メールアドレスを入力してください'
                    value={state.email}
                    onChange={onChange}
                    aria-invalid={!!errors.email}
                    className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/60'
                  />
                  {errors.email && (
                    <p className='mt-1 text-sm text-rose-600'>{errors.email}</p>
                  )}
                </div>

                {/* 種別 */}
                <div>
                  <label htmlFor='type' className='block text-sm font-bold text-gray-800'>
                    お問い合わせ種別<span className='ml-1 text-rose-500'>*</span>
                  </label>
                  <select
                    id='type'
                    name='type'
                    value={state.type}
                    onChange={onChange}
                    aria-invalid={!!errors.type}
                    className='mt-2 w-full appearance-none border border-gray-200 bg-white px-3 py-3 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-secondary/60'
                  >
                    <option value=''>選択してください</option>
                    {inquiryTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className='mt-1 text-sm text-rose-600'>{errors.type}</p>
                  )}
                </div>

                {/* ご参加区分・紹介者（イベント参加/入会希望のときのみ） */}
                {isReferralTargetType(state.type) && (
                  <div className='border border-line bg-gray-50/60 p-4'>
                    <div>
                      <label
                        htmlFor='attendeeType'
                        className='block text-sm font-bold text-gray-800'
                      >
                        ご自身の区分<span className='ml-1 text-rose-500'>*</span>
                      </label>
                      <select
                        id='attendeeType'
                        name='attendeeType'
                        value={state.attendeeType}
                        onChange={onChange}
                        aria-invalid={!!errors.attendeeType}
                        className='mt-2 w-full appearance-none border border-gray-200 bg-white px-3 py-3 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-secondary/60'
                      >
                        <option value=''>選択してください</option>
                        {ATTENDEE_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      {errors.attendeeType && (
                        <p className='mt-1 text-sm text-rose-600'>
                          {errors.attendeeType}
                        </p>
                      )}
                    </div>

                    {state.attendeeType === FIRST_TIME_ATTENDEE_TYPE && (
                      <div className='mt-4'>
                        <label
                          htmlFor='referrerName'
                          className='block text-sm font-bold text-gray-800'
                        >
                          どなたかのご紹介ですか？
                          <span className='ml-2 text-xs font-normal text-gray-500'>
                            任意
                          </span>
                        </label>
                        <input
                          id='referrerName'
                          name='referrerName'
                          type='text'
                          placeholder='紹介者のお名前'
                          value={state.referrerName}
                          onChange={onChange}
                          disabled={state.noReferrer}
                          className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/60 disabled:bg-gray-100 disabled:text-gray-400'
                        />
                        <div className='mt-2 flex items-center gap-2'>
                          <input
                            id='noReferrer'
                            name='noReferrer'
                            type='checkbox'
                            checked={state.noReferrer}
                            onChange={onChange}
                            className='h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary'
                          />
                          <label
                            htmlFor='noReferrer'
                            className='text-sm text-gray-700'
                          >
                            紹介者はいません
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 本文 */}
                <div>
                  <label htmlFor='message' className='block text-sm font-bold text-gray-800'>
                    お問い合わせ内容<span className='ml-1 text-rose-500'>*</span>
                  </label>
                  <textarea
                    id='message'
                    name='message'
                    rows={6}
                    placeholder='お問い合わせ内容をご記入ください'
                    value={state.message}
                    onChange={onChange}
                    aria-invalid={!!errors.message}
                    className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/60'
                  />
                  {errors.message && (
                    <p className='mt-1 text-sm text-rose-600'>{errors.message}</p>
                  )}
                </div>

                {/* 同意 */}
                <div className='flex items-start gap-3'>
                  <input
                    id='agree'
                    name='agree'
                    type='checkbox'
                    checked={state.agree}
                    onChange={onChange}
                    aria-invalid={!!errors.agree}
                    className='mt-1 h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary'
                  />
                  <label htmlFor='agree' className='text-sm text-gray-700'>
                    プライバシーポリシーに同意します
                    <span className='ml-1 text-rose-500'>*</span>
                    <a href='/privacy' className='ml-2 link-underline text-secondary'>
                      プライバシーポリシーを確認する
                    </a>
                  </label>
                </div>
                {errors.agree && (
                  <p className='-mt-4 text-sm text-rose-600'>{errors.agree}</p>
                )}

                {/* 送信 */}
                <div className='pt-2'>
                  <button
                    type='submit'
                    disabled={submitting}
                    className='inline-flex min-w-32 items-center justify-center bg-linear-to-r from-primary to-primary/60 px-6 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {submitting ? '送信中...' : '送信する'}
                  </button>
                  {doneMessage && (
                    <p className='mt-3 text-sm text-emerald-700'>
                      {doneMessage}
                    </p>
                  )}
                </div>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
